import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Grid } from '@mui/material';
import { FormDatePicker } from 'components/molecules/FormDatePicker';
import { FormRadioGroup } from 'components/molecules/FormRadioGroup';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import { SearchWrapper } from 'components/atoms/SearchWrapper';
import { useTranslations } from 'hooks/useTranslations';
import {
  ISearchLegalAttachmentsFormValues,
  initialLegalAttachmentsSearchFormValues,
} from 'models/form';
import { ELegalAttachmentsGroupBy } from 'models/legalEntity';
import { formatDateTimeToIso } from 'utils/date';

interface IAttachmentsSearchFormProps {
  onSubmit: (formValues: ISearchLegalAttachmentsFormValues) => void;
  isSearchButtonDisabled?: boolean;
  initialFormValues?: ISearchLegalAttachmentsFormValues;
  onAddAttachment?: () => void;
  licenceAuthorityValid: boolean;
  showLicenceColumns: boolean;
}

export const AttachmentsSearchForm: React.FC<IAttachmentsSearchFormProps> = ({
  onSubmit,
  isSearchButtonDisabled = false,
  initialFormValues = initialLegalAttachmentsSearchFormValues,
  onAddAttachment,
  licenceAuthorityValid,
  showLicenceColumns,
}) => {
  const { t } = useTranslations();

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ISearchLegalAttachmentsFormValues>({
    defaultValues: initialFormValues,
  });

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'groupBy') {
        onSubmit(value as ISearchLegalAttachmentsFormValues);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, onSubmit]);

  const groupByOptions = [
    {
      label: t('legalEntityDetails.attachments.groupBy.none'),
      value: ELegalAttachmentsGroupBy.NONE,
    },
    {
      label: t('legalEntityDetails.attachments.groupBy.number'),
      value: ELegalAttachmentsGroupBy.NUMBER,
    },
    {
      label: t('legalEntityDetails.attachments.groupBy.attachmentType'),
      value: ELegalAttachmentsGroupBy.ATTACHMENT_TYPE,
    },
  ];

  const onSubmitForm = async (data: any) => {
    if (data.createDateTo) {
      const date = formatDateTimeToIso(data.createDateTo, true);
      data.createDateTo = date;
    }
    await onSubmit(data);
  };

  return (
    <>
      <SearchWrapper>
        <form
          onSubmit={handleSubmit(onSubmitForm)}
          id="attachments-search-form"
          data-testid="attachments-search-form"
        >
          <Grid container spacing={2} columns={6} sx={{ justifyContent: 'space-between' }}>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormDatePicker
                control={control}
                name="createDateFrom"
                label={t('legalEntityDetails.attachments.search.createDateFrom')}
                errors={errors}
                data-testid="createDateFrom"
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <FormDatePicker
                control={control}
                name="createDateTo"
                label={t('legalEntityDetails.attachments.search.createDateTo')}
                errors={errors}
                data-testid="createDateTo"
              />
            </Grid>
            <Grid size={{ xs: 10, sm: 5, md: 5, lg: 2 }}>
              <Button
                type="submit"
                variant={EButtonVariants.contained}
                size={EButtonSizes.small}
                label={t('general.search.label')}
                disabled={isSearchButtonDisabled}
                data-testid="search-button"
                fullWidth
                sx={{ mt: 3.5 }}
              />
            </Grid>
          </Grid>
        </form>
      </SearchWrapper>
      <Grid
        container
        spacing={2}
        sx={{
          mt: 2,
          justifyContent: showLicenceColumns ? 'space-between' : 'flex-end',
          alignItems: 'flex-end',
        }}
      >
        {showLicenceColumns && (
          <Grid size={{ xs: 'auto' }}>
            <FormRadioGroup
              control={control}
              name="groupBy"
              label={t('legalEntityDetails.attachments.search.groupBy')}
              options={groupByOptions}
              errors={errors}
              data-testid="groupBy"
            />
          </Grid>
        )}

        {onAddAttachment && licenceAuthorityValid && (
          <Grid size={{ xs: 'auto' }}>
            <Button
              id="legal_entity_details_attachments-add"
              label={t('legalEntityDetails.attachments.search.addNewAttachment')}
              variant={EButtonVariants.contained}
              icon={EIconTypes.plus}
              onClick={onAddAttachment}
              size={EButtonSizes.small}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};
